/** @format */

import React from "react";
import { useTranslation } from "react-i18next";
import {
	Container,
	Typography,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	styled,
	Box,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Footer, SEO } from "../allComponents/AllComponents";

const PageContainer = styled(Container)(({ theme }) => ({
	paddingTop: theme.spacing(8),
	paddingBottom: theme.spacing(8),
	fontFamily: "'Inter', sans-serif",
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
	marginTop: theme.spacing(6),
	marginBottom: theme.spacing(3),
	fontFamily: "'Montserrat', sans-serif",
	fontWeight: 600,
	color: theme.palette.primary.main,
}));

interface FaqItem {
	question: string;
	answer: string;
}

interface FaqSection {
	title: string;
	answer?: string;
	sections?: Array<{
		subtitle: string;
		content: string;
	}>;
	description?: string;
}

const FAQPage = () => {
	const { t, i18n } = useTranslation("pages");

	const faqs = t("faq.faqs", { returnObjects: true }) as Array<FaqItem>;

	const definition = t("faq.definition", { returnObjects: true }) as FaqSection;
	const whyUseFundsyFi = t("faq.whyUseFundsyFi", { returnObjects: true }) as FaqSection;
	const typesOfCircles = t("faq.typesOfCircles", { returnObjects: true }) as FaqSection;
	const legalCompliance = t("faq.legalCompliance", { returnObjects: true }) as FaqSection;
	const trustSafety = t("faq.trustSafety", { returnObjects: true }) as FaqSection;
	const paymentMethods = t("faq.paymentMethods", { returnObjects: true }) as FaqSection;
	const payoutMethods = t("faq.payoutMethods", { returnObjects: true }) as FaqSection;
	const feesInfo = t("faq.feesInfo", { returnObjects: true }) as FaqSection;

	return (
		<React.Fragment>
			<SEO
				title={t("faq.title")}
				description={t("faq.subtitle")}
				keywords="savings circle FAQ, how to join circle, contribution schedule, savings questions, group savings help, FundsyFi support"
				url="/faq"
			/>
			<PageContainer maxWidth="md">
				<Typography
					variant="h3"
					gutterBottom
					sx={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 600 }}>
					{t("faq.title")}
				</Typography>
				<Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
					{t("faq.subtitle")}
				</Typography>

				{/* Definition Section */}
				{definition && (
					<Box sx={{ mb: 4 }}>
						<SectionTitle variant="h5">{definition.title}</SectionTitle>
						{definition.answer && (
							<Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
								{definition.answer}
							</Typography>
						)}
					</Box>
				)}

				{/* Why Use FundsyFi Section */}
				{whyUseFundsyFi && (
					<Box sx={{ mb: 4 }}>
						<SectionTitle variant="h5">{whyUseFundsyFi.title}</SectionTitle>
						{whyUseFundsyFi.sections && whyUseFundsyFi.sections.length > 0 ? (
							whyUseFundsyFi.sections.map((item, idx) => (
								<Accordion key={`why-${idx}`}>
									<AccordionSummary expandIcon={<ExpandMoreIcon />}>
										<Typography variant="h6">{item.subtitle}</Typography>
									</AccordionSummary>
									<AccordionDetails>
										<Typography>{item.content}</Typography>
									</AccordionDetails>
								</Accordion>
							))
						) : (
							<Typography variant="body1" sx={{ mb: 2 }}>
								{whyUseFundsyFi.description}
							</Typography>
						)}
					</Box>
				)}

				{/* Types of Circles Section */}
				{typesOfCircles && (
					<Box sx={{ mb: 4 }}>
						<SectionTitle variant="h5">{typesOfCircles.title}</SectionTitle>
						{typesOfCircles.sections && typesOfCircles.sections.length > 0 ? (
							typesOfCircles.sections.map((item, idx) => (
								<Accordion key={`type-${idx}`}>
									<AccordionSummary expandIcon={<ExpandMoreIcon />}>
										<Typography variant="h6">{item.subtitle}</Typography>
									</AccordionSummary>
									<AccordionDetails>
										<Typography>{item.content}</Typography>
									</AccordionDetails>
								</Accordion>
							))
						) : (
							<Typography variant="body1" sx={{ mb: 2 }}>
								{typesOfCircles.description}
							</Typography>
						)}
					</Box>
				)}

				{/* Legal & Compliance Section */}
				{legalCompliance && (
					<Box sx={{ mb: 4 }}>
						<SectionTitle variant="h5">{legalCompliance.title}</SectionTitle>
						{legalCompliance.sections && legalCompliance.sections.length > 0 ? (
							legalCompliance.sections.map((item, idx) => (
								<Accordion key={`legal-${idx}`}>
									<AccordionSummary expandIcon={<ExpandMoreIcon />}>
										<Typography variant="h6">{item.subtitle}</Typography>
									</AccordionSummary>
									<AccordionDetails>
										<Typography>{item.content}</Typography>
									</AccordionDetails>
								</Accordion>
							))
						) : (
							<Typography variant="body1" sx={{ mb: 2 }}>
								{legalCompliance.description}
							</Typography>
						)}
					</Box>
				)}

				{/* Trust & Safety Section */}
				{trustSafety && (
					<Box sx={{ mb: 4 }}>
						<SectionTitle variant="h5">{trustSafety.title}</SectionTitle>
						{trustSafety.sections && trustSafety.sections.length > 0 ? (
							trustSafety.sections.map((item, idx) => (
								<Accordion key={`trust-${idx}`}>
									<AccordionSummary expandIcon={<ExpandMoreIcon />}>
										<Typography variant="h6">{item.subtitle}</Typography>
									</AccordionSummary>
									<AccordionDetails>
										<Typography>{item.content}</Typography>
									</AccordionDetails>
								</Accordion>
							))
						) : (
							<Typography variant="body1" sx={{ mb: 2 }}>
								{trustSafety.description}
							</Typography>
						)}
					</Box>
				)}

				{/* Payment Methods Section */}
				{paymentMethods && (
					<Box sx={{ mb: 4 }}>
						<SectionTitle variant="h5">{paymentMethods.title}</SectionTitle>
						{paymentMethods.sections && paymentMethods.sections.length > 0 ? (
							paymentMethods.sections.map((item, idx) => (
								<Accordion key={`payment-${idx}`}>
									<AccordionSummary expandIcon={<ExpandMoreIcon />}>
										<Typography variant="h6">{item.subtitle}</Typography>
									</AccordionSummary>
									<AccordionDetails>
										<Typography>{item.content}</Typography>
									</AccordionDetails>
								</Accordion>
							))
						) : (
							<Typography variant="body1" sx={{ mb: 2 }}>
								{paymentMethods.description}
							</Typography>
						)}
					</Box>
				)}

				{/* Payout Methods Section */}
				{payoutMethods && (
					<Box sx={{ mb: 4 }}>
						<SectionTitle variant="h5">{payoutMethods.title}</SectionTitle>
						{payoutMethods.sections && payoutMethods.sections.length > 0 ? (
							payoutMethods.sections.map((item, idx) => (
								<Accordion key={`payout-${idx}`}>
									<AccordionSummary expandIcon={<ExpandMoreIcon />}>
										<Typography variant="h6">{item.subtitle}</Typography>
									</AccordionSummary>
									<AccordionDetails>
										<Typography>{item.content}</Typography>
									</AccordionDetails>
								</Accordion>
							))
						) : (
							<Typography variant="body1" sx={{ mb: 2 }}>
								{payoutMethods.description}
							</Typography>
						)}
					</Box>
				)}

				{/* Fees Info Section */}
				{feesInfo && (
					<Box sx={{ mb: 4 }}>
						<SectionTitle variant="h5">{feesInfo.title}</SectionTitle>
						{feesInfo.sections && feesInfo.sections.length > 0 ? (
							feesInfo.sections.map((item, idx) => (
								<Accordion key={`fees-${idx}`}>
									<AccordionSummary expandIcon={<ExpandMoreIcon />}>
										<Typography variant="h6">{item.subtitle}</Typography>
									</AccordionSummary>
									<AccordionDetails>
										<Typography>{item.content}</Typography>
									</AccordionDetails>
								</Accordion>
							))
						) : (
							<Typography variant="body1" sx={{ mb: 2 }}>
								{feesInfo.description}
							</Typography>
						)}
					</Box>
				)}

				{/* Original FAQs */}
				{faqs && faqs.length > 0 ? (
					<Box sx={{ mt: 6 }}>
						<SectionTitle variant="h5">
							{t("faq.title")}
						</SectionTitle>
						{faqs.map((faq, index) => (
							<Accordion key={index}>
								<AccordionSummary expandIcon={<ExpandMoreIcon />}>
									<Typography variant="h6">{faq.question}</Typography>
								</AccordionSummary>
								<AccordionDetails>
									<Typography>{faq.answer}</Typography>
								</AccordionDetails>
							</Accordion>
						))}
					</Box>
				) : (
					<Typography variant="body1" color="text.secondary">
						No FAQs available
					</Typography>
				)}
			</PageContainer>
			<Footer />
		</React.Fragment>
	);
};

export default FAQPage;
